import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

type DotLottieController = {
  addEventListener: (event: string, handler: () => void) => void;
  removeEventListener: (event: string, handler: () => void) => void;
  play: () => void;
  setFrame: (frame: number) => void;
  setSpeed: (speed: number) => void;
  stop: () => void;
};

export type AdventureButtonHandle = {
  playFromStart: () => void;
  reset: () => void;
};

interface AdventureButtonProps {
  className?: string;
  disabled?: boolean;
  onClick: () => void;
  style?: React.CSSProperties;
}

const BUTTON_LOTTIE_SRC = `${process.env.PUBLIC_URL}/animations/button.lottie`;
const DotLottieCanvas = DotLottieReact as React.ComponentType<{
  autoplay?: boolean;
  className?: string;
  dotLottieRefCallback?: (player: DotLottieController | null) => void;
  layout?: { align?: [number, number]; fit?: "contain" | "cover" | "fill" };
  loop?: boolean;
  renderConfig?: { autoResize?: boolean };
  src: string;
  useFrameInterpolation?: boolean;
}>;

const AdventureButton = forwardRef<AdventureButtonHandle, AdventureButtonProps>(
  ({ className, disabled = false, onClick, style }, ref) => {
    const dotLottieRef = useRef<DotLottieController | null>(null);
    const isLoadedRef = useRef(false);
    const pendingPlayRef = useRef(false);
    const lastPlayStartedAtRef = useRef(0);

    const requestPlay = useCallback(() => {
      const now = performance.now();
      if (now - lastPlayStartedAtRef.current < 180) {
        return;
      }

      const player = dotLottieRef.current;
      if (!player || !isLoadedRef.current) {
        pendingPlayRef.current = true;
        return;
      }

      lastPlayStartedAtRef.current = now;
      pendingPlayRef.current = false;
      player.stop();
      player.setFrame(0);
      player.play();
    }, []);

    const resetToFirstFrame = useCallback(() => {
      const player = dotLottieRef.current;
      if (!player) return;

      player.stop();
      player.setFrame(0);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        playFromStart: () => {
          requestPlay();
        },
        reset: () => {
          pendingPlayRef.current = false;
          lastPlayStartedAtRef.current = 0;
          resetToFirstFrame();
        },
      }),
      [requestPlay, resetToFirstFrame]
    );

    const handleReady = useCallback(() => {
      isLoadedRef.current = true;
      resetToFirstFrame();

      if (!pendingPlayRef.current) return;

      requestPlay();
    }, [requestPlay, resetToFirstFrame]);

    const attachPlayer = useCallback((player: DotLottieController | null) => {
      if (dotLottieRef.current) {
        dotLottieRef.current.removeEventListener("load", resetToFirstFrame);
        dotLottieRef.current.removeEventListener("ready", handleReady);
      }

      dotLottieRef.current = player;
      isLoadedRef.current = false;

      if (!player) return;

      player.setSpeed(1.08);
      player.addEventListener("load", resetToFirstFrame);
      player.addEventListener("ready", handleReady);
    }, [handleReady, resetToFirstFrame]);

    return (
      <button
        type="button"
        onClick={onClick}
        className={className}
        style={{
          ...style,
          background: "transparent",
          border: "none",
          padding: 0,
        }}
        disabled={disabled}
        aria-label="Launch a random internet adventure"
        onMouseDown={() => {
          if (!disabled) {
            requestPlay();
          }
        }}
        onTouchStart={() => {
          if (!disabled) {
            requestPlay();
          }
        }}
        onKeyDown={(event) => {
          if (!disabled && (event.key === "Enter" || event.key === " ")) {
            requestPlay();
          }
        }}
      >
        <div className="relative h-full w-full overflow-visible">
          <div className="absolute left-1/2 top-1/2 h-[152%] w-[152%] -translate-x-1/2 -translate-y-[38%] transform">
            <DotLottieCanvas
              src={BUTTON_LOTTIE_SRC}
              autoplay={false}
              loop={false}
              useFrameInterpolation
              dotLottieRefCallback={attachPlayer}
              className="h-full w-full"
              layout={{ fit: "cover", align: [0.5, 0.68] }}
              renderConfig={{ autoResize: true }}
            />
          </div>
        </div>
      </button>
    );
  }
);

AdventureButton.displayName = "AdventureButton";

export default AdventureButton;
